import { isPlatformBrowser } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { Inject, Injectable, PLATFORM_ID } from '@angular/core';
import { Router } from '@angular/router';
import * as moment from 'moment';
import { BehaviorSubject, EMPTY, Observable, Subject, forkJoin, switchMap, tap } from 'rxjs';
import { FeedbackService } from './feedback.service';
import { GroupService } from './group.service';
import { UserService } from './user.service';

export type PaystackCheckoutResult =
  | { success: true; subscription: any }
  | { success: false; cancelled: true }
  | { success: false; error: string };

@Injectable({
  providedIn: 'root'
})
export class SubscriptionService {

  private apiUrl = 'api/subscription';

  private subscriptionSubject = new BehaviorSubject<any>(null);
  subscription$ = this.subscriptionSubject.asObservable();

  private invoicesSubject = new BehaviorSubject<any>(null);
  invoices$ = this.invoicesSubject.asObservable();

  private transactionsSubject = new BehaviorSubject<any>(null);
  transactions$ = this.transactionsSubject.asObservable();

  constructor(
    private http: HttpClient,
    private router: Router,
    private userService: UserService,
    private groupService: GroupService,
    private feedbackService: FeedbackService,
    @Inject(PLATFORM_ID) private platformId: Object
  ) {
    this.groupService.selectedGroup$.pipe(
      switchMap(selectedGroup => {
        if (selectedGroup) {
          return forkJoin([this.getSubscriptionByGroup(selectedGroup.id), this.getTransactionsByGroup(selectedGroup.id)]);
        }
        return EMPTY;
      })
    ).subscribe();
  }

  getSubscriptionByGroup(groupId: string): Observable<any> {
    const token = localStorage.getItem('token');
    return this.http.get(`${this.apiUrl}/group/${groupId}`, { headers: { Authorization: `Bearer ${token}` } })
      .pipe(
        tap(async (results: any) => {
          const subscription = results.data;
          this.subscriptionSubject.next(subscription);

          if (!subscription || subscription.status !== 'COMPLETE') {
            let forceSubscription = false;
            let updateDetails = false;
            let message = `Your subscription is in an invalid state and you will lose access on ${moment(subscription.nextBillingDate).format('DD/MM/YYYY')} if you dont take action. Would you like to subscribe now to continue managing your own patient list?`;

            if (subscription.status === 'RETRY') {
              if (subscription.retryCount > 3) {
                forceSubscription = true;
              }
              updateDetails = true;

              // Server cancels when retryCount > 3 on the next billing run, retries daily.
              // Worst-case days left = (nextBillingDate - today) + remaining retries.
              const MAX_RETRIES = 4;
              const retryCount = subscription.retryCount || 1;
              const remainingRetries = Math.max(0, MAX_RETRIES - retryCount);
              const cancellationDate = moment(subscription.nextBillingDate).startOf('day').add(remainingRetries, 'days');
              const daysLeft = Math.max(0, cancellationDate.diff(moment().startOf('day'), 'days'));

              if (daysLeft <= 0) {
                message = 'We experienced a problem charging your most recent subscription payment and your final retry is due today. Please update your card details immediately to avoid cancellation.';
              } else {
                const dayLabel = daysLeft === 1 ? 'day' : 'days';
                message = `We experienced a problem charging your most recent subscription payment. You have ${daysLeft} ${dayLabel} left to update your payment details before your subscription is cancelled. Would you like to update your card details now?`;
              }
            } else if (subscription.status === 'PENDING' || !subscription) {
              forceSubscription = true;
              message = 'A subscription is required to manage your own patient list. Would you like to subscribe now?'
            } else if (moment(subscription.nextBillingDate).isSameOrBefore(moment(), 'day')) {
              forceSubscription = true;
              message = 'Your subscription has lapsed and you will no longer be able to access MedicAI features until you subscribe. Would you like to subscribe now to continue managing your own patient list?'
            }

            const confirmed = await this.feedbackService.showConfirmation('Subscription Required', message);;
            if (confirmed) {
              if (forceSubscription) {
                this.groupService.setSelectedGroup(null);
              }
              if (updateDetails) {
                this.groupService.updateCardDetails.next(true);
              } else {
                this.groupService.processNewSubscription.next(true);
              }
              this.router.navigate(['/settings']);
            } else if (forceSubscription) {
              // One navigation to a fresh /login — see `UserService.logoutAndRedirect`.
              this.userService.logoutAndRedirect();
            } else {
              this.router.navigate(['/settings']);
            }
          }

        })
      );
  }

  getInvoicesByGroup(groupId: string): Observable<any> {
    const token = localStorage.getItem('token');
    return this.http.get(`${this.apiUrl}/invoices/group/${groupId}`, { headers: { Authorization: `Bearer ${token}` } })
      .pipe(
        tap(async (results: any) => {
          this.invoicesSubject.next(results.data);
        })
      );
  }

  getTransactionsByGroup(groupId: string): Observable<any> {
    const token = localStorage.getItem('token');
    return this.http.get(`${this.apiUrl}/transactions/group/${groupId}`, { headers: { Authorization: `Bearer ${token}` } })
      .pipe(
        tap(async (results: any) => {
          this.transactionsSubject.next(results.data);
        })
      );
  }

  cancelSubscription(groupId: string): Observable<any> {
    const token = localStorage.getItem('token');
    return this.http.post(`${this.apiUrl}/cancel/${groupId}`, { groupId }, {
      headers: { Authorization: `Bearer ${token}` }
    });
  }

  processTokenRecharge(groupId: string, amount: number): Observable<any> {
    const token = localStorage.getItem('token');
    return this.http.post(`${this.apiUrl}/token-recharge`, { groupId, amount }, {
      headers: { Authorization: `Bearer ${token}` }
    });
  }

  processTextMessageCreditRecharge(groupId: string, amount: number): Observable<any> {
    const token = localStorage.getItem('token');
    return this.http.post(`${this.apiUrl}/text-message-credit-recharge`, { groupId, amount }, {
      headers: { Authorization: `Bearer ${token}` }
    });
  }

  updateAutoRechargeSettings(groupId: string, settings: any): Observable<any> {
    const token = localStorage.getItem('token');
    return this.http.post(`${this.apiUrl}/configure/auto-recharge`, { groupId, settings }, {
      headers: { Authorization: `Bearer ${token}` }
    });
  }


  updateTextMessageAutoRechargeSettings(groupId: string, settings: any): Observable<any> {
    const token = localStorage.getItem('token');
    return this.http.post(`${this.apiUrl}/configure/text-message-auto-recharge`, { groupId, settings }, {
      headers: { Authorization: `Bearer ${token}` }
    });
  }

  updateInvoiceDetails(groupId: string, settings: any): Observable<any> {
    const token = localStorage.getItem('token');
    return this.http.post(`${this.apiUrl}/configure/invoice-details`, { groupId, settings }, {
      headers: { Authorization: `Bearer ${token}` }
    });
  }

  validatePromoCode(code: string, userSpecialty?: string): Observable<any> {
    const token = localStorage.getItem('token');
    const user: any = this.userService.user;
    return this.http.post(`api/promo-code/validate`, {
      code,
      userId: user?.id,
      userSpecialty: userSpecialty || user?.speciality
    }, {
      headers: { Authorization: `Bearer ${token}` }
    });
  }

  /**
   * Open Paystack popup to start a new subscription. Server initializes the transaction
   * (no merchant credentials in client); on success we verify server-side which persists
   * the subscription with provider='paystack' and the encrypted authorization code.
   */
  processNewSubscription(
    invoiceDetails: any,
    promoCodeDetails?: { id: string; discountAmount?: number; discountType?: string },
    subscriptionTier: 'standard' | 'premium' = 'standard',
  ): Observable<PaystackCheckoutResult> {
    const token = localStorage.getItem('token');
    const body = {
      invoiceDetails,
      promoCodeId: promoCodeDetails?.id || undefined,
      subscriptionTier,
    };
    return this.http
      .post<{ success: boolean; access_code: string; reference: string }>(
        `${this.apiUrl}/paystack/initialize`,
        body,
        { headers: { Authorization: `Bearer ${token}` } },
      )
      .pipe(switchMap((init) => this.runPaystackPopupAndVerify(init.access_code, init.reference)));
  }

  /**
   * Open Paystack popup to update the card on file for an existing group's subscription.
   * Server flips the subscription's provider to 'paystack' and stores the new authorization.
   */
  processCardUpdate(groupId: string): Observable<PaystackCheckoutResult> {
    const token = localStorage.getItem('token');
    return this.http
      .post<{ success: boolean; access_code: string; reference: string }>(
        `${this.apiUrl}/paystack/initialize-card-update/${groupId}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } },
      )
      .pipe(switchMap((init) => this.runPaystackPopupAndVerify(init.access_code, init.reference)));
  }

  private runPaystackPopupAndVerify(accessCode: string, reference: string): Observable<PaystackCheckoutResult> {
    if (!isPlatformBrowser(this.platformId)) {
      return EMPTY;
    }

    const subject = new Subject<PaystackCheckoutResult>();

    const verify = () => {
      const token = localStorage.getItem('token');
      this.http
        .post<{ success: boolean; subscription?: any }>(
          `${this.apiUrl}/paystack/verify`,
          { reference },
          { headers: { Authorization: `Bearer ${token}` } },
        )
        .subscribe({
          next: (res) => {
            if (res?.success) {
              subject.next({ success: true, subscription: res.subscription });
            } else {
              subject.next({ success: false, error: 'verify_failed' });
            }
            subject.complete();
          },
          error: (err) => {
            subject.next({ success: false, error: err?.error?.message || 'verify_error' });
            subject.complete();
          },
        });
    };

    import('@paystack/inline-js').then(({ default: PaystackPop }) => {
      const popup = new PaystackPop();
      popup.resumeTransaction(accessCode, {
        onSuccess: () => verify(),
        onCancel: () => {
          subject.next({ success: false, cancelled: true });
          subject.complete();
        },
        onError: (err: any) => {
          subject.next({ success: false, error: err?.message || 'paystack_popup_error' });
          subject.complete();
        },
      });
    }).catch((err) => {
      subject.next({ success: false, error: err?.message || 'paystack_load_error' });
      subject.complete();
    });

    return subject.asObservable();
  }

  handleSubscriptionResponse(success: boolean, status: string) {
    const urlTree = this.router.createUrlTree(['/settings'], {
      queryParams: {
        'subSuccess': success
      },
      queryParamsHandling: 'merge',
      preserveFragment: true
    });

    this.router.navigateByUrl(urlTree);
  }

  upgradeDowngradeSubscription(groupId: string, tier: 'standard' | 'premium'): Observable<any> {
    const token = localStorage.getItem('token');
    return this.http.post(`${this.apiUrl}/upgrade-downgrade/${groupId}`, { tier }, {
      headers: { Authorization: `Bearer ${token}` }
    });
  }

  /**
   * Enable MedicAI Connect for the group.
   * @param groupId - Group id
   * @param payload - { userIds?, shareSubscription?, chargeType?, autoRechargeThreshold?, autoRechargeAmount? }
   *   userIds: array of clinical user ids to grant access (owner always included server-side)
   *   shareSubscription: if true, Connect claim costs are billed to the card on the clinical subscription
   *   chargeType: when shareSubscription, 'pay_as_you_go' or 'monthly'
   *   autoRechargeThreshold, autoRechargeAmount: when shareSubscription && chargeType pay_as_you_go (Connect-specific)
   */
  enableConnectForGroup(groupId: string, payload: { userIds?: string[]; userRoles?: Record<string, string>; shareSubscription?: boolean; billingDayOfMonth?: number; chargeType?: 'pay_as_you_go' | 'monthly'; autoRechargeThreshold?: number; autoRechargeAmount?: number }): Observable<any> {
    const token = localStorage.getItem('token');
    const body = {
      userIds: payload?.userIds ?? [],
      ...(payload?.userRoles && { userRoles: payload.userRoles }),
      ...(payload?.shareSubscription !== undefined && { shareSubscription: payload.shareSubscription }),
      ...(payload?.billingDayOfMonth != null && { billingDayOfMonth: payload.billingDayOfMonth }),
      ...(payload?.chargeType && { chargeType: payload.chargeType }),
      ...(payload?.autoRechargeThreshold != null && { autoRechargeThreshold: payload.autoRechargeThreshold }),
      ...(payload?.autoRechargeAmount != null && { autoRechargeAmount: payload.autoRechargeAmount }),
    };
    return this.http.post(`${this.apiUrl}/connect-enable/${groupId}`, body, {
      headers: { Authorization: `Bearer ${token}` }
    });
  }
}
